import {
  DEBUG_QUERY_INITIAL,
  DEFAULT_ACTION,
  INPUT_IMAGE_FIELDS,
  MAX_BATCH_SIZE,
  MIN_BATCH_SIZE,
  REVIEW_SESSION_DRAFT_STORAGE_KEY,
  REVIEW_SESSION_STORAGE_KEY,
  REVIEW_SESSION_VERSION
} from "./constants";
import type {
  ErrorLogEntry,
  EvalImage,
  EvalQueryParams,
  FilterValues,
  ImageEvalState,
  ReviewSession,
  ReviewSessionBase,
  ReviewSessionDraft,
  SerializedImageEvalState
} from "../../types/creative-eval";

export function defaultEvalState(): ImageEvalState {
  return { categories: new Set(), details: {}, severity: null, notes: null };
}

export function toISODate(date: Date): string {
  return date.toISOString().split("T")[0];
}

export function rangeFromMonths(monthsBack: number): Pick<EvalQueryParams, "date_start" | "date_end"> {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - monthsBack);

  return {
    date_start: toISODate(start),
    date_end: toISODate(end)
  };
}

export function clampBatchSize(size: number): number {
  return Math.max(MIN_BATCH_SIZE, Math.min(MAX_BATCH_SIZE, size));
}

export function countInputImages(images: EvalImage[]): number {
  return images.reduce(
    (count, image) => count + INPUT_IMAGE_FIELDS.reduce((fieldCount, field) => fieldCount + image[field].length, 0),
    0
  );
}

export function formatErrorLog(entries: ErrorLogEntry[]): string {
  if (entries.length === 0) return "No errors recorded.";

  return entries
    .toReversed()
    .map((entry) => {
      const lines = [`[${new Date(entry.time).toLocaleString()}] ${entry.context}`, entry.message];
      if (entry.detail) lines.push(entry.detail);
      return lines.join("\n");
    })
    .join("\n\n");
}

export function getPreviewPosition(clientX: number, clientY: number) {
  const pad = 20;
  const previewWidth = 470;
  const previewHeight = 460;
  let x = clientX + pad;
  let y = clientY - 225;

  if (x + previewWidth > window.innerWidth) x = clientX - previewWidth;
  if (y < 10) y = 10;
  if (y + previewHeight > window.innerHeight) y = window.innerHeight - previewHeight;

  return { x, y };
}

export function findPreviewImage(eventTarget: EventTarget): HTMLImageElement | null {
  if (!(eventTarget instanceof Element)) return null;

  const directImage = eventTarget.closest(".card-image, .input-thumb");
  if (directImage instanceof HTMLImageElement && directImage.src) return directImage;

  const imageButton = eventTarget.closest(".card-image-button, .input-thumb-button");
  const nestedImage = imageButton?.querySelector("img");
  return nestedImage instanceof HTMLImageElement && nestedImage.src ? nestedImage : null;
}

export function toSnakeCase(str: string): string {
  return str.toLowerCase().replaceAll(" ", "_");
}

export function formatLabel(key: string): string {
  return key.replaceAll("_", " ").replaceAll(/\b\w/g, (c) => c.toUpperCase());
}

export function serializeEvalStates(
  evalStates: Record<number, ImageEvalState>
): Record<string, SerializedImageEvalState> {
  return Object.fromEntries(
    Object.entries(evalStates).map(([creativeId, state]) => [
      creativeId,
      {
        categories: [...state.categories],
        details: state.details,
        severity: state.severity,
        notes: state.notes
      }
    ])
  );
}

export function deserializeEvalStates(
  evalStates: Record<string, SerializedImageEvalState> | undefined
): Record<number, ImageEvalState> {
  if (!evalStates) return {};

  return Object.fromEntries(
    Object.entries(evalStates).map(([creativeId, state]) => [
      Number(creativeId),
      {
        categories: new Set(Array.isArray(state.categories) ? state.categories : []),
        details: state.details || {},
        severity: state.severity || null,
        notes: state.notes || null
      }
    ])
  );
}

export function hasEvaluationSelections(evalStates: Record<number, ImageEvalState>): boolean {
  return Object.values(evalStates).some((state) => state.categories.size > 0);
}

function writeSession(key: string, value: object): void {
  try {
    sessionStorage.setItem(
      key,
      JSON.stringify({
        ...value,
        version: REVIEW_SESSION_VERSION
      })
    );
  } catch {
    // sessionStorage full or unavailable
  }
}

function readSession<T extends { version: number }>(key: string): Partial<T> | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<T>;
    return parsed.version === REVIEW_SESSION_VERSION ? parsed : null;
  } catch {
    return null;
  }
}

export const saveReviewSessionBase = (session: Omit<ReviewSessionBase, "version">): void =>
  writeSession(REVIEW_SESSION_STORAGE_KEY, session);

export const saveReviewSessionDraft = (draft: Omit<ReviewSessionDraft, "version">): void =>
  writeSession(REVIEW_SESSION_DRAFT_STORAGE_KEY, draft);

function loadReviewSessionDraft(): ReviewSessionDraft | null {
  const parsed = readSession<ReviewSessionDraft>(REVIEW_SESSION_DRAFT_STORAGE_KEY);
  if (!parsed) return null;

  return {
    version: REVIEW_SESSION_VERSION,
    evalStates: parsed.evalStates || {},
    hasUnsavedChanges: Boolean(parsed.hasUnsavedChanges)
  };
}

export function loadReviewSession(): ReviewSession | null {
  const parsed = readSession<ReviewSessionBase>(REVIEW_SESSION_STORAGE_KEY);
  if (!parsed?.queryParams?.brand_id || !parsed.filters?.brandId || !Array.isArray(parsed.images)) return null;
  if (parsed.filters.brandId !== parsed.queryParams.brand_id) return null;
  const draft = loadReviewSessionDraft();

  return {
    version: REVIEW_SESSION_VERSION,
    filters: parsed.filters,
    queryParams: parsed.queryParams,
    images: parsed.images,
    nextPageToken: parsed.nextPageToken ?? null,
    debugQuery: parsed.debugQuery || DEBUG_QUERY_INITIAL,
    evalStates: draft?.evalStates || {},
    hasUnsavedChanges: Boolean(draft?.hasUnsavedChanges)
  };
}

export function clearReviewSession(): void {
  try {
    sessionStorage.removeItem(REVIEW_SESSION_STORAGE_KEY);
    sessionStorage.removeItem(REVIEW_SESSION_DRAFT_STORAGE_KEY);
  } catch {
    // sessionStorage unavailable
  }
}

// ---------------------------------------------------------------------------
// Filter / query-param helpers (pure, no React)
// ---------------------------------------------------------------------------

export function buildQueryParamsFromFilters(filters: FilterValues): EvalQueryParams | null {
  if (!filters.brandId) return null;

  const customDateRange = filters.dateStart || filters.dateEnd;
  const dateRange = customDateRange
    ? {
        ...(filters.dateStart ? { date_start: filters.dateStart } : {}),
        ...(filters.dateEnd ? { date_end: filters.dateEnd } : {})
      }
    : rangeFromMonths(filters.monthsBack);

  return {
    brand_id: filters.brandId,
    ...dateRange,
    actions: filters.selectedActions.length ? filters.selectedActions.join(",") : DEFAULT_ACTION,
    user_type: "CUSTOMER",
    limit: clampBatchSize(filters.batchSize),
    ...(filters.imageId ? { image_id: Number(filters.imageId) } : {}),
    ...(filters.userId ? { user_id: Number(filters.userId) } : {}),
    ...(filters.includeEvaluated ? { include_evaluated: true } : {}),
    ...(filters.randomize ? { randomize: true } : { randomize: false })
  };
}

export function cloneFilters(filters: FilterValues): FilterValues {
  return { ...filters, selectedActions: [...filters.selectedActions] };
}

export function queryParamsEqual(a: EvalQueryParams | null, b: EvalQueryParams | null): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

// ---------------------------------------------------------------------------
// Dev tools query builders
// ---------------------------------------------------------------------------

export function buildInspectLabelsQuery(creativeIds: number[]): string {
  if (creativeIds.length === 0) return "";
  const sortedIds = [...new Set(creativeIds)].sort((a, b) => a - b);
  const whereClause =
    sortedIds.length === 1 ? `cel.creative_id = ${sortedIds[0]}` : `cel.creative_id IN (${sortedIds.join(", ")})`;

  return [
    "SELECT",
    "    cel.id,",
    "    cel.creative_id,",
    "    cel.brand_id,",
    "    cel.evaluator_id,",
    "    u.username AS evaluator,",
    "    cel.evaluation,",
    "    JSON_UNQUOTE(JSON_EXTRACT(cel.evaluation, '$.notes')) AS notes,",
    "    CONVERT_TZ(cel.created_at, '+00:00', 'America/Los_Angeles') AS created_at_pst",
    "FROM creative_eval_labels cel",
    "LEFT JOIN users u ON u.id = cel.evaluator_id",
    `WHERE ${whereClause};`
  ].join("\n");
}
